import type { z } from 'zod'
import { env } from '@/app/config/env'
import { ApiError } from './api-error'
import { API_SUCCESS_CODE, getApiResponseMessage } from './api-response-code'
import { clearMemberSession, getMemberToken, setMemberPasswordChangeRequired } from '@/entities/member/model/member-session'

interface RequestOptions<T> extends Omit<RequestInit, 'body'> {
  body?: unknown
  schema: z.ZodType<T>
  timeoutMs?: number
}

function requestUrl(path: string) {
  if (/^https?:\/\//.test(path)) return path
  return `${env.apiBaseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`
}

export async function request<T>(path: string, options: RequestOptions<T>): Promise<T> {
  const { body, schema, headers, timeoutMs = 15_000, signal, ...init } = options
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs)
  const abort = () => controller.abort()
  signal?.addEventListener('abort', abort, { once: true })

  try {
    const requestInit: RequestInit = {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}), ...(getMemberToken() ? { Authorization: `Bearer ${getMemberToken()}` } : {}), ...headers },
    }
    if (body !== undefined) requestInit.body = JSON.stringify(body)
    const response = await fetch(requestUrl(path), requestInit)
    const payload: unknown = response.status === 204 ? undefined : await response.json()
    const errorPayload = payload as { code?: string; message?: string; details?: unknown; requestId?: string } | undefined

    if (errorPayload?.code === 'UNAUTHORIZED') {
      clearMemberSession()
      throw new ApiError(getApiResponseMessage(errorPayload.code, errorPayload.message), response.status, errorPayload.code, errorPayload.details, response.headers.get('x-request-id') ?? errorPayload.requestId)
    }

    if (errorPayload?.code === 'PASSWORD_CHANGE_REQUIRED') {
      setMemberPasswordChangeRequired(true)
      throw new ApiError(getApiResponseMessage(errorPayload.code, errorPayload.message), response.status, errorPayload.code, errorPayload.details, response.headers.get('x-request-id') ?? errorPayload.requestId)
    }

    if (errorPayload?.code && errorPayload.code !== API_SUCCESS_CODE) {
      throw new ApiError(getApiResponseMessage(errorPayload.code, errorPayload.message), response.status, errorPayload.code, errorPayload.details, response.headers.get('x-request-id') ?? errorPayload.requestId)
    }

    if (!response.ok) {
      const error = errorPayload ?? {}
      if (response.status === 401) clearMemberSession()
      throw new ApiError(getApiResponseMessage(error.code, error.message, `请求失败：${response.status}`), response.status, error.code, error.details, response.headers.get('x-request-id') ?? error.requestId)
    }
    return schema.parse(payload)
  } finally {
    window.clearTimeout(timeout)
    signal?.removeEventListener('abort', abort)
  }
}
